import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs as MUIBreadcrumbs, Link, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

/**
 * Breadcrumbs component for SEO-friendly navigation
 * Automatically generates breadcrumbs based on current URL path
 * Also adds structured data for search engines
 */
const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  // Map of route segments to human-readable names
  const routeNameMap = {
    'financial': 'Financial Calculators',
    'math': 'Math Calculators',
    'fitness': 'Fitness Calculators',
    'other': 'Other Calculators',
    'calculators': 'Calculators',
    'mortgage': 'Mortgage Calculator',
    'investment': 'Investment Calculator',
    'retirement': 'Retirement Calculator',
    'scientific': 'Scientific Calculator',
    'percentage': 'Percentage Calculator',
    'bmi': 'BMI Calculator',
    'calorie': 'Calorie Calculator',
    'unit-conversion': 'Unit Conversion',
    'amortization': 'Amortization Calculator',
    'interest': 'Interest Calculator',
    'about': 'About Us',
    'contact': 'Contact Us',
    'faq': 'FAQ',
    'privacy': 'Privacy Policy',
    'terms': 'Terms of Service',
    'blog': 'Blog'
  };
  
  // Generate structured data for breadcrumbs
  const generateStructuredData = () => {
    const baseUrl = 'https://www.solverwatch.com';
    const itemListElement = [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': baseUrl
      }
    ];
    
    let path = '';
    pathnames.forEach((value, index) => {
      path += `/${value}`;
      itemListElement.push({
        '@type': 'ListItem',
        'position': index + 2,
        'name': routeNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1),
        'item': `${baseUrl}${path}`
      });
    });
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': itemListElement
    };
  };
  
  return (
    <>
      <MUIBreadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2, mt: 1 }}
      >
        <Link 
          component={RouterLink} 
          to="/"
          color="inherit"
          display="flex"
          alignItems="center"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const name = routeNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1);
          
          return last ? (
            <Typography color="text.primary" key={to}>
              {name}
            </Typography>
          ) : (
            <Link component={RouterLink} to={to} key={to} color="inherit">
              {name}
            </Link>
          );
        })}
      </MUIBreadcrumbs>
      
      {/* Add structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData())}
      </script>
    </>
  );
};

export default Breadcrumbs;
